import { FC, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogActions, useMediaQuery, useTheme } from '@mui/material';

import { Button } from '@components/base';
import { FilterGroup } from '@models/SearchFilter';
import useSearchAndFilter from '@modules/layouts/hooks/useSearchAndFilter';

import useFilterDialog from './hooks/useFilterDialog';
import FilterDialogContextProvider from './hooks/useFilterDialog/provider';
import AddRootLevelFilterGroupButton from './AddRootLevelFilterGroupButton';
import FilterDialogContent from './FilterDialogContent';

type FilterDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  updateFilter: (newFilter: FilterGroup) => void;
};

const FilterDialogInner: FC<FilterDialogProps> = ({ isOpen, onClose, updateFilter }) => {
  const { searchFilter } = useFilterDialog();
  const { t } = useTranslation();
  const theme = useTheme();
  const xsScreen = useMediaQuery(theme.breakpoints.only('xs'));

  const {
    filterDefLoad: { data: filterDefinitions },
    isReadonly,
  } = useSearchAndFilter();

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    updateFilter(searchFilter);
    onClose();
  };

  return (
    <Dialog
      fullScreen={xsScreen}
      fullWidth
      maxWidth='md'
      onClose={onClose}
      open={isOpen}
      slotProps={{ paper: { sx: { overflowY: 'initial' } } }}
    >
      <form onSubmit={onSubmit}>
        <FilterDialogContent />

        {isReadonly ? (
          <DialogActions>
            <Button onClick={onClose}>{t('close')}</Button>
          </DialogActions>
        ) : (
          filterDefinitions && (
            <DialogActions>
              <AddRootLevelFilterGroupButton />

              <Button onClick={onClose}>{t('cancel')}</Button>
              <Button type='submit' variant='contained'>
                {t('apply')}
              </Button>
            </DialogActions>
          )
        )}
      </form>
    </Dialog>
  );
};

const FilterDialog: FC<FilterDialogProps> = props => {
  const { isOpen } = props;

  return (
    <FilterDialogContextProvider isOpen={isOpen}>
      <FilterDialogInner {...props} />
    </FilterDialogContextProvider>
  );
};

export default FilterDialog;
